// src/pages/SiteEditor.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebsiteBuilder } from '../contexts/WebsiteBuilderContext';
import { ArrowLeft, Eye, ChevronDown, ChevronRight, X, ExternalLink } from 'lucide-react';
import WebsitePreview from './preview/WebsitePreview';
import useUserTier from '../hooks/useUserTier';
import PublishModal from '../components/site-editor/PublishModal';
import UpgradeModal from '../components/site-editor/UpgradeModal';
import SlugSelectionModal from '../components/SlugSelectionModal';
import PublishSuccessModal from '../components/PublishSuccessModal';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db, isFirebaseInitialized } from '../config/firebase-config';
import { claimSlug, changeSlug } from '../utils/slug-utils';
import { trackEvent } from '../utils/analytics';
import { getPractitionerDisplayName } from '../utils/getPractitionerDisplayName';

// Import all panels
import {
  ColorSchemePanel,
  TypographyPanel,
  LayoutPanel,
  ButtonStylePanel,
  ScrollAnimationPanel,
  HeroBackgroundPanel,
  HeroTextPanel,
  SocialLinksPanel,
  AboutPanel,
  ServicesPanel,
  FAQsPanel,
  TestimonialsPanel,
  GalleryPanel,
  EventsPanel,
  ContactPanel,
  LogoPanel,
  FooterPanel,
  PoliciesPanel,
  NewsletterPanel
} from '../components/site-editor/panels';

const SiteEditor = () => {
  const navigate = useNavigate();
  const { state, actions } = useWebsiteBuilder();
  const { isDirty, lastPublished, saveStatus } = state;
  const userTier = useUserTier();

  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState('design');
  const [activePanel, setActivePanel] = useState(null);
  const [customBackHandler, setCustomBackHandler] = useState(null);
  const [showPublishDropdown, setShowPublishDropdown] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSlugModal, setShowSlugModal] = useState(false);
  const [showChangeSlugModal, setShowChangeSlugModal] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [practitionerSlug, setPractitionerSlug] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [firestoreOk, setFirestoreOk] = useState(true);
  const displayName = getPractitionerDisplayName(state);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editTabHovered, setEditTabHovered] = useState(false);
  const previewScrollRef = useRef(null);
  const publishDropdownRef = useRef(null);

  // Close publish dropdown when clicking outside
  useEffect(() => {
    if (!showPublishDropdown) return;
    const handleClickOutside = (e) => {
      if (publishDropdownRef.current && !publishDropdownRef.current.contains(e.target)) {
        setShowPublishDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPublishDropdown]);

  // Detect mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // Mobile/tablet below 1024px
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load practitioner slug on mount so "View Live Site" works on page reload.
  // Uses onAuthStateChanged to handle Firebase Auth's ~300ms async restore.
  useEffect(() => {
    if (!isFirebaseInitialized()) return;
    const authObj = getAuth();
    let done = false;
    const unsubscribe = onAuthStateChanged(authObj, async (user) => {
      if (done || !user) return;
      done = true;
      try {
        const userDoc = await getDoc(doc(db, 'practitioners', user.uid));
        if (userDoc.exists() && userDoc.data().slug) {
          setPractitionerSlug(userDoc.data().slug);
        }
      } catch { /* non-critical */ }
    });
    const timeout = setTimeout(() => { done = true; unsubscribe(); }, 5000);
    return () => { done = true; unsubscribe(); clearTimeout(timeout); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll preview to active section when panel changes
  useEffect(() => {
    if (!activePanel || !previewScrollRef.current) return;
    const panelToSection = {
      background: 'hero', text: 'hero',
      services: 'services', faqs: 'faqs', testimonials: 'testimonials',
      gallery: 'gallery', events: 'events', newsletter: 'newsletter', contact: 'contact',
      social: 'footer', footer: 'footer', policies: 'footer',
    };
    const sectionId = panelToSection[activePanel];
    if (!sectionId) return;
    const container = previewScrollRef.current;
    const target = container.querySelector(`[data-section="${sectionId}"]`);
    if (target) {
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const scrollOffset = container.scrollTop + (targetTop - containerTop) - 16;
      container.scrollTo({ top: Math.max(0, scrollOffset), behavior: 'smooth' });
    }
  }, [activePanel]);

  // Handle social OAuth callback returns (?social=platform_connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('social')) {
      setActiveCategory('contact-footer');
      setActivePanel('social');
      setSidebarOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar categories
  const categories = [
    { id: 'design', label: 'Design', panels: ['colors', 'typography', 'layout', 'buttons', 'logo', 'animations'] },
    { id: 'hero', label: 'Hero', panels: ['background', 'text'] },
    { id: 'content', label: 'Content', panels: ['about', 'services', 'faqs', 'testimonials', 'gallery', 'events'] },
    { id: 'contact-footer', label: 'Contact & Footer', panels: ['contact', 'social', 'footer', 'policies'] },
    { id: 'marketing', label: 'Marketing', panels: ['newsletter'] }
  ];

  // Panel component mapping
  const panelComponents = {
    // Design
    colors: ColorSchemePanel,
    typography: TypographyPanel,
    layout: LayoutPanel,
    buttons: ButtonStylePanel,
    animations: ScrollAnimationPanel,
    // Hero
    background: HeroBackgroundPanel,
    text: HeroTextPanel,
    // Content
    about: AboutPanel,
    services: ServicesPanel,
    faqs: FAQsPanel,
    testimonials: TestimonialsPanel,
    gallery: GalleryPanel,
    events: EventsPanel,
    // Contact & Footer
    contact: ContactPanel,
    social: SocialLinksPanel,
    logo: LogoPanel,
    footer: FooterPanel,
    policies: PoliciesPanel,
    // Marketing
    newsletter: NewsletterPanel
  };

  const ActivePanelComponent = activePanel ? panelComponents[activePanel] : null;

  const handlePublishClick = () => {
    setShowPublishDropdown(false);
    setPublishError(null);

    // Check tier and show appropriate modal
    if (userTier === 'free') {
      setShowUpgradeModal(true);
    } else {
      setShowPublishModal(true);
    }
  };

  const handlePublishConfirm = async () => {
    // On first publish, check if the practitioner has claimed a slug.
    // If not, show SlugSelectionModal before publishing.
    let existingSlug = null;
    try {
      if (isFirebaseInitialized()) {
        const auth = getAuth();
        const uid = auth?.currentUser?.uid;
        if (uid) {
          const userDoc = await getDoc(doc(db, 'practitioners', uid));
          const data = userDoc.exists() ? userDoc.data() : {};
          if (!data?.slug) {
            setShowPublishModal(false);
            setShowSlugModal(true);
            return;
          }
          existingSlug = data.slug;
        }
      }
    } catch (err) {
      console.warn('Slug check failed, proceeding without slug:', err);
    }

    setShowPublishModal(false);
    setPublishing(true);
    setPublishError(null);
    try {
      const ok = await actions.publish();
      setFirestoreOk(ok !== false);
      setPractitionerSlug(existingSlug);
      setShowPublishSuccess(true);
      trackEvent('site_published', { template: state?.template || '' });
    } catch (err) {
      console.error('Publish failed:', err);
      setPublishError('Unable to publish — please sign in again and try once more.');
    } finally {
      setPublishing(false);
    }
  };

  const handleSlugConfirm = async (slug) => {
    try {
      const auth = getAuth();
      const uid = auth?.currentUser?.uid;
      if (uid) {
        await claimSlug(slug, uid);
      }
    } catch (err) {
      console.error('Failed to claim slug:', err);
    }
    setShowSlugModal(false);
    setPublishing(true);
    setPublishError(null);
    try {
      const ok = await actions.publish();
      setFirestoreOk(ok !== false);
      setPractitionerSlug(slug);
      setShowPublishSuccess(true);
    } catch (err) {
      console.error('Publish failed:', err);
      setPublishError('Unable to publish — please sign in again and try once more.');
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscard = () => {
    if (window.confirm('Discard all unpublished changes? This cannot be undone.')) {
      actions.discard();
      setShowPublishDropdown(false);
    }
  };

  const handleOpenChangeUrl = () => {
    setShowPublishSuccess(false);
    setShowChangeSlugModal(true);
  };

  const handleChangeUrlConfirm = async (newSlug) => {
    setShowChangeSlugModal(false);
    try {
      const auth = getAuth();
      const uid = auth?.currentUser?.uid;
      if (uid) {
        await changeSlug(practitionerSlug, newSlug, uid);
      }
      setPractitionerSlug(newSlug);
    } catch (err) {
      console.error('Failed to change URL:', err);
    }
    // Re-show success modal so practitioner sees their new URL
    setShowPublishSuccess(true);
  };

  const handlePreview = () => {
    // Toggle sidebar open/closed
    setSidebarOpen(!sidebarOpen);
  };

  const handleSectionClick = (panelId) => {
    // Find which category this panel belongs to
    const category = categories.find(cat => cat.panels.includes(panelId));
    if (category) {
      setActiveCategory(category.id);
      setActivePanel(panelId);
      // Open sidebar if it's collapsed
      if (!sidebarOpen) {
        setSidebarOpen(true);
      }
    }
  };

  // Map active panel to section ID for highlighting
  const getActiveSectionFromPanel = () => {
    const sectionMap = {
      background: 'hero',
      text: 'hero',
      colors: 'design',
      typography: 'design',
      layout: 'design',
      buttons: 'design',
      services: 'services',
      faqs: 'faqs',
      testimonials: 'testimonials',
      gallery: 'gallery',
      newsletter: 'newsletter',
      social: 'footer',
      footer: 'footer'
    };
    return activePanel ? sectionMap[activePanel] : null;
  };

  // Mobile gate
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Your Site Editor works best on a desktop or laptop
            </h1>
            <p className="text-slate-600 mb-6">
              Building your site requires more screen space. But there's plenty you can do from your phone:
            </p>
          </div>

          <div className="space-y-3 mb-8">
            <button
              onClick={() => navigate('/preview')}
              className="w-full p-4 bg-white border border-slate-200 rounded-xl hover:border-purple-300 transition-colors text-left"
            >
              <div className="font-semibold text-slate-900 mb-1">View Your Live Site</div>
              <div className="text-sm text-slate-600">See how your site looks to clients</div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full p-4 bg-white border border-slate-200 rounded-xl hover:border-purple-300 transition-colors text-left"
            >
              <div className="font-semibold text-slate-900 mb-1">Check Bookings & Messages</div>
              <div className="text-sm text-slate-600">Manage your practice on the go</div>
            </button>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="text-purple-600 hover:text-purple-700 font-medium text-sm"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Desktop/tablet editor
  return (
    <div className="site-editor h-screen flex flex-col bg-slate-50">
      {/* Top Bar — z-index 60 ensures publish dropdown appears above the preview's sticky nav (z-50) */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" style={{ position: 'relative', zIndex: 60 }}>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="w-px h-6 bg-slate-300" />
          <h1 className="text-xl font-bold text-slate-900">Site Editor</h1>
        </div>

        <div className="flex items-center gap-3">
          {/* View Live Site — only shown once the site has been published */}
          {lastPublished && practitionerSlug && (
            <a
              href={
                window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                  ? `${window.location.origin}/site/${practitionerSlug}`
                  : `https://divineframeworks.com/site/${practitionerSlug}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-purple-600 border border-slate-200 hover:border-purple-300 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Live Site
            </a>
          )}

          <button
            onClick={handlePreview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              sidebarOpen
                ? 'text-slate-700 hover:text-slate-900 border border-transparent hover:border-slate-300 hover:shadow-md'
                : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg hover:scale-105'
            }`}
          >
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">{sidebarOpen ? 'Preview' : 'Edit'}</span>
          </button>

          {/* Publish Dropdown */}
          <div className="relative" ref={publishDropdownRef}>
            <button
              onClick={() => setShowPublishDropdown(!showPublishDropdown)}
              disabled={!isDirty}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium text-sm transition-colors ${
                isDirty
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <span>Publish</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showPublishDropdown && isDirty && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-[70]">
                <button
                  onClick={handlePublishClick}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <div className="font-medium text-slate-900">Publish Changes</div>
                  <div className="text-xs text-slate-600">Make your changes live</div>
                </button>
                <div className="border-t border-slate-200 my-2" />
                <button
                  onClick={handleDiscard}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <div className="font-medium">Discard Changes</div>
                  <div className="text-xs text-red-600/80">Revert to published version</div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar */}
        <div
          className="site-editor-sidebar bg-white border-r border-slate-200 flex flex-col"
          style={{
            width: sidebarOpen ? '340px' : '0px',
            minWidth: sidebarOpen ? '340px' : '0px',
            maxWidth: sidebarOpen ? '340px' : '0px',
            flexShrink: 0,
            flexGrow: 0,
            flexBasis: sidebarOpen ? '340px' : '0px',
            transition: 'all 0.3s ease-in-out',
            overflow: 'hidden'
          }}
        >
          {/* Panel Header - shows when panel is active */}
          {activePanel && ActivePanelComponent ? (
            <>
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <button
                  onClick={() => {
                    if (customBackHandler) {
                      customBackHandler();
                    } else {
                      setActivePanel(null);
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <ActivePanelComponent setCustomBackHandler={setCustomBackHandler} onNavigate={setActivePanel} />
              </div>
            </>
          ) : (
            <>
              <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <h2 className="font-bold text-[#111827]" style={{ fontSize: '18px', fontWeight: 700 }}>
                  Customize Your Site
                </h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="border-b border-[#E5E7EB] mx-4" />

              <div className="flex-1 overflow-y-auto">
                {categories.map((category) => (
                  <div key={category.id} className="border-b border-slate-100">
                    <button
                      onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
                      className={`relative w-full px-4 py-3 text-left transition-all duration-150 flex items-center justify-between ${
                        activeCategory === category.id
                          ? 'bg-slate-50 border-l-[3px] border-l-[#7c3aed]'
                          : 'hover:bg-[#F9FAFB] border-l-[3px] border-l-transparent'
                      }`}
                    >
                      <span className="font-semibold text-[15px] text-[#1f2937]">
                        {category.label}
                      </span>
                      <ChevronRight
                        className={`w-4 h-4 text-[#6b7280] transition-transform duration-200 ${
                          activeCategory === category.id ? 'rotate-90' : ''
                        }`}
                      />
                    </button>

                    {activeCategory === category.id && (
                      <div className="bg-slate-50 py-2">
                        {category.panels.map((panel) => (
                          <button
                            key={panel}
                            onClick={() => setActivePanel(panel)}
                            className={`w-full pl-8 pr-4 py-2 text-left text-sm font-normal transition-colors ${
                              activePanel === panel
                                ? 'text-[#7c3aed] font-medium'
                                : 'text-[#4b5563] hover:text-[#7c3aed]'
                            }`}
                            style={{ fontSize: '14px' }}
                          >
                            {panel === 'faqs' ? 'FAQs' : panel.charAt(0).toUpperCase() + panel.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Left-edge EDIT tab (shows when sidebar is collapsed) */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            onMouseEnter={() => setEditTabHovered(true)}
            onMouseLeave={() => setEditTabHovered(false)}
            className="absolute bg-purple-600 text-white rounded-r-lg transition-all duration-200 z-10 cursor-pointer"
            style={{
              left: 0,
              top: '50%',
              transform: editTabHovered
                ? 'translateY(-50%) translateX(4px) scale(1.05)'
                : 'translateY(-50%)',
              padding: '24px 12px',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              lineHeight: 1,
              boxShadow: editTabHovered
                ? '0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.15)'
                : '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              backgroundColor: editTabHovered ? '#6d28d9' : '#7c3aed'
            }}
          >
            EDIT
          </button>
        )}

        {/* Preview Area — isolation:isolate creates a stacking context that contains
             all WebsiteRenderer section z-indexes (labels z-10, nav z-50, etc.)
             so they cannot bleed above the top bar (z-60), dropdown (z-70), or modals (z-100) */}
        <div ref={previewScrollRef} className="flex-1 overflow-auto bg-slate-100 p-8" style={{ isolation: 'isolate' }}>
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <WebsitePreview
                embedded={true}
                onSectionClick={handleSectionClick}
                activeSection={getActiveSectionFromPanel()}
                activePanel={activePanel}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-white border-t border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${publishing ? 'bg-purple-500' : isDirty || !lastPublished ? 'bg-amber-500' : 'bg-green-500'}`} />
              <span className="text-slate-700">
                {publishing ? 'Publishing…' : !lastPublished ? 'Draft — never published' : isDirty ? 'Draft (unpublished changes)' : 'Published and live'}
              </span>
            </div>
            {lastPublished && (
              <span className="text-slate-500">
                Last published: {new Date(lastPublished).toLocaleString()}
              </span>
            )}
          </div>
          {publishError ? (
            <span className="font-medium text-red-600">{publishError}</span>
          ) : saveStatus ? (
            <span className={`font-medium ${
              saveStatus === 'saving' ? 'text-amber-600' :
              saveStatus === 'saved' ? 'text-green-600' :
              saveStatus === 'error' ? 'text-red-600' : ''
            }`}>
              {saveStatus === 'saving' && 'Auto-saving draft...'}
              {saveStatus === 'saved' && 'Draft saved'}
              {saveStatus === 'error' && 'Save failed'}
            </span>
          ) : null}
        </div>
      </div>

      {/* Publish Modal (Pro/Elite tiers) */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        hasUnpublishedChanges={isDirty}
      />

      {/* Upgrade Modal (Free tier) */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      {/* Slug Selection Modal (first publish — claim public URL) */}
      <SlugSelectionModal
        isOpen={showSlugModal}
        practitionerName={displayName}
        onConfirm={handleSlugConfirm}
        onCancel={() => setShowSlugModal(false)}
      />

      {/* Publish Success Modal */}
      <PublishSuccessModal
        isOpen={showPublishSuccess}
        onClose={() => setShowPublishSuccess(false)}
        slug={practitionerSlug}
        onChangeUrl={practitionerSlug ? handleOpenChangeUrl : undefined}
        firestoreOk={firestoreOk}
      />

      {/* Change URL Modal */}
      <SlugSelectionModal
        isOpen={showChangeSlugModal}
        mode="change"
        currentSlug={practitionerSlug}
        practitionerName={displayName}
        onConfirm={handleChangeUrlConfirm}
        onCancel={() => {
          setShowChangeSlugModal(false);
          setShowPublishSuccess(true); // return to success modal
        }}
      />
    </div>
  );
};

export default SiteEditor;
